/**
 * Admin Test Call Modal — Send demo calls from any Retell agent
 * No daily cap, no logging. For sales demos.
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';

interface Agent {
  agent_id: string;
  agent_name: string;
}

interface Phone {
  phone_number: string;
  nickname: string;
}

interface AdminTestCallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AdminTestCallModal({ isOpen, onClose }: AdminTestCallModalProps) {
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedFromNumber, setSelectedFromNumber] = useState('');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [phones, setPhones] = useState<Phone[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  // Fetch agents and phones on open
  useEffect(() => {
    if (!isOpen) return;
    const fetchData = async () => {
      setLoadingData(true);
      try {
        const [agentRes, phoneRes] = await Promise.all([
          httpsCallable(functions, 'listRetellAgents')({}),
          httpsCallable(functions, 'listRetellPhones')({}),
        ]);
        const agentData = agentRes.data as { agents: Agent[] };
        const phoneData = phoneRes.data as { phones: Phone[] };
        setAgents(agentData.agents || []);
        setPhones(phoneData.phones || []);
      } catch (err) {
        console.error('Failed to load agents/phones:', err);
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setPhone('');
    setFirstName('');
    setSelectedAgent('');
    setSelectedFromNumber('');
    setResult(null);
    onClose();
  };

  const handleSend = async () => {
    if (!phone || !firstName || !selectedAgent || !selectedFromNumber) return;
    setSending(true);
    setResult(null);

    try {
      const fn = httpsCallable(functions, 'adminTestCall');
      await fn({
        phone,
        firstName,
        agentId: selectedAgent,
        fromNumber: selectedFromNumber,
      });
      setResult({ success: true, message: 'Call sent! The agent is calling now.' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setResult({ success: false, message });
    } finally {
      setSending(false);
    }
  };

  const selectedAgentName = agents.find(a => a.agent_id === selectedAgent)?.agent_name || '';

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={handleClose}>
      <div style={{
        background: 'var(--dark-2)', borderRadius: '12px', padding: '32px',
        maxWidth: '480px', width: '90%', border: '1px solid var(--dark-3)',
        maxHeight: '90vh', overflowY: 'auto',
      }} onClick={e => e.stopPropagation()}>

        <h2 style={{ margin: '0 0 4px', color: 'var(--white)' }}>Send Demo Call</h2>
        <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '20px' }}>
          Pick an agent and phone number, then send a test call to anyone.
        </p>

        {loadingData ? (
          <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--gray-400)' }}>
            Loading agents and phones...
          </div>
        ) : (
          <>
            {/* Agent selector */}
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label style={{ color: 'var(--gray-300)', fontSize: '0.85rem' }}>Agent</label>
              <select
                value={selectedAgent}
                onChange={e => setSelectedAgent(e.target.value)}
                style={{
                  width: '100%', padding: '10px', background: 'var(--dark)',
                  border: '1px solid var(--dark-3)', borderRadius: '6px',
                  color: 'var(--white)', fontFamily: 'inherit', fontSize: '0.9rem',
                }}
              >
                <option value="">Select an agent...</option>
                {agents.map(a => (
                  <option key={a.agent_id} value={a.agent_id}>{a.agent_name || a.agent_id}</option>
                ))}
              </select>
            </div>

            {/* From number selector */}
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label style={{ color: 'var(--gray-300)', fontSize: '0.85rem' }}>From Number</label>
              <select
                value={selectedFromNumber}
                onChange={e => setSelectedFromNumber(e.target.value)}
                style={{
                  width: '100%', padding: '10px', background: 'var(--dark)',
                  border: '1px solid var(--dark-3)', borderRadius: '6px',
                  color: 'var(--white)', fontFamily: 'inherit', fontSize: '0.9rem',
                }}
              >
                <option value="">Select a phone number...</option>
                {phones.map(p => (
                  <option key={p.phone_number} value={p.phone_number}>
                    {p.phone_number}{p.nickname ? ` — ${p.nickname}` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* To phone */}
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label style={{ color: 'var(--gray-300)', fontSize: '0.85rem' }}>Call This Number</label>
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="(214) 555-1234"
                style={{
                  width: '100%', padding: '10px', background: 'var(--dark)',
                  border: '1px solid var(--dark-3)', borderRadius: '6px',
                  color: 'var(--white)', fontFamily: 'inherit', fontSize: '1rem',
                }}
              />
            </div>

            {/* Name */}
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ color: 'var(--gray-300)', fontSize: '0.85rem' }}>First Name (agent will use this)</label>
              <input
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                placeholder="Their Name"
                style={{
                  width: '100%', padding: '10px', background: 'var(--dark)',
                  border: '1px solid var(--dark-3)', borderRadius: '6px',
                  color: 'var(--white)', fontFamily: 'inherit', fontSize: '1rem',
                }}
              />
            </div>

            <button
              onClick={handleSend}
              disabled={sending || !phone || !firstName || !selectedAgent || !selectedFromNumber}
              className="btn btn-primary"
              style={{ width: '100%', marginBottom: '10px', padding: '12px' }}
            >
              {sending ? 'Calling...' : `Send Call${selectedAgentName ? ` — ${selectedAgentName}` : ''}`}
            </button>
          </>
        )}

        {result && (
          <div style={{
            padding: '12px 14px', borderRadius: '8px', marginBottom: '12px',
            background: result.success ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${result.success ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
            color: result.success ? '#10b981' : '#dc2626',
            fontSize: '0.85rem', textAlign: 'center',
          }}>
            {result.message}
          </div>
        )}

        <button onClick={handleClose} className="btn btn-secondary" style={{ width: '100%' }}>
          {result?.success ? 'Done' : 'Cancel'}
        </button>
      </div>
    </div>
  );
}
